import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { signUp } from "../../../services/operations/authAPI";
import { endpoints } from "../../../services/apis";
import toast from "react-hot-toast";
import { FiEye, FiEyeOff } from "react-icons/fi";

export default function Signup() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
    class: "",
    phoneno: "", // Already initialized here

    // Parent fields
    studentId: "", // For linking to existing student
    relationshipType: "guardian",

    // Student fields
    invitationCode: "", // For joining via parent invitation

    // Child details (when parent registers first)
    childName: "",
    childClass: "",
    expectedChildEmail: "", // Optional: child's expected email
  });

  const [linkingMode, setLinkingMode] = useState(""); // "existing" or "create_invite"
  const [registrationMode, setRegistrationMode] = useState(""); // "independent" or "join_invitation"
  const [showPassword, setShowPassword] = useState(false);
  const [validation, setValidation] = useState({
    studentId: { isValid: null, message: "", studentName: "" },
    invitationCode: { isValid: null, message: "", parentName: "" },
  });

  const [isValidating, setIsValidating] = useState(false);
  const [emailValid, setEmailValid] = useState(true);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const validateEmail = (email) => {
    // Allow only emails ending with @mnrtechnologies.com
    // const regex = /^[\w.-]+@mnrtechnologies\.com$/i;
    const regex = /^[\w.-]+@mnr-pst\.com$/i;
    return regex.test(email);
  };

  // Validate student ID for parents
  const validateStudentId = async (studentId) => {
    if (!studentId || studentId.length < 6) {
      setValidation((prev) => ({
        ...prev,
        studentId: { isValid: null, message: "", studentName: "" },
      }));
      return;
    }

    setIsValidating(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/auth/validate-student/${studentId}`
      );
      const data = await response.json();

      setValidation((prev) => ({
        ...prev,
        studentId: {
          isValid: data.success,
          message: data.success
            ? `Student found: ${data.studentName} (Class ${data.class})`
            : "Student ID not found",
          studentName: data.studentName || "",
        },
      }));
    } catch (error) {
      setValidation((prev) => ({
        ...prev,
        studentId: {
          isValid: false,
          message: "Error validating student ID",
          studentName: "",
        },
      }));
    } finally {
      setIsValidating(false);
    }
  };

  // Validate invitation code for students
  const validateInvitationCode = async (code) => {
    if (!code || code.length < 6) {
      setValidation((prev) => ({
        ...prev,
        invitationCode: { isValid: null, message: "", parentName: "" },
      }));
      return;
    }

    setIsValidating(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/auth/validate-invitation/${code}`
      );
      const data = await response.json();

      setValidation((prev) => ({
        ...prev,
        invitationCode: {
          isValid: data.success,
          message: data.success
            ? `Invitation from: ${data.parentName}`
            : "Invalid or expired invitation code",
          parentName: data.parentName || "",
        },
      }));

      // Auto-fill child details if available
      if (data.success && data.childDetails) {
        setForm((prev) => ({
          ...prev,
          name: data.childDetails.expectedName || prev.name,
          class: data.childDetails.expectedClass || prev.class,
        }));
      }
    } catch (error) {
      setValidation((prev) => ({
        ...prev,
        invitationCode: {
          isValid: false,
          message: "Error validating invitation code",
          parentName: "",
        },
      }));
    } finally {
      setIsValidating(false);
    }
  };

  const handleStudentIdChange = (e) => {
    const value = e.target.value;
    setForm({ ...form, studentId: value });

    clearTimeout(window.studentIdTimeout);
    window.studentIdTimeout = setTimeout(() => validateStudentId(value), 500);
  };

  const handleInvitationCodeChange = (e) => {
    const value = e.target.value;
    setForm({ ...form, invitationCode: value });

    clearTimeout(window.invitationTimeout);
    window.invitationTimeout = setTimeout(
      () => validateInvitationCode(value),
      500
    );
  };

  const validatePassword = (password) => {
    const isValidLength = password.length >= 12;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return (
      isValidLength &&
      hasUppercase &&
      hasLowercase &&
      hasNumber &&
      hasSpecialChar
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // if (!validateEmail(form.email)) {
    //   toast.error("This email is not allow.Connect to MNR team");
    //   return;
    // }

    if (!validatePassword(form.password)) {
      toast.error(
        "Password must be at least 12 characters long and include uppercase, lowercase, numbers, and special characters"
      );
      return;
    }

    // Validate parent linking
    if (form.role === "parent") {
      if (linkingMode === "existing" && validation.studentId.isValid !== true) {
        alert("Please enter a valid student ID to continue.");
        return;
      }
      if (
        linkingMode === "create_invite" &&
        (!form.childName || !form.childClass)
      ) {
        alert("Please provide child's name and class to create invitation.");
        return;
      }
    }

    // Validate student joining
    if (form.role === "student" && registrationMode === "join_invitation") {
      if (validation.invitationCode.isValid !== true) {
        alert("Please enter a valid invitation code to continue.");
        return;
      }
    }

    const signupData = {
      ...form,
      linkingMode,
      registrationMode,
    };

    dispatch(signUp(signupData, navigate, -1, endpoints.ADD_USER_API));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 text-[#04254d] px-4 pt-4 pb-4">
      <div className="w-full max-w-md bg-white shadow-md rounded-xl p-8">
        <h2 className="text-2xl font-semibold mb-6 text-center">
          Create Account
        </h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Basic Fields */}
          <div>
            <label className="block text-sm mb-1 font-medium">Full Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              placeholder="Enter your full name"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#04254d]"
            />
          </div>

          <div>
            <label className="block text-sm mb-1 font-medium">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => {
                const value = e.target.value;
                setForm({ ...form, email: value });

               // setEmailValid(validateEmail(value));
              }}
              required
              placeholder="Enter your mail address"
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-[#04254d] ${
                emailValid ? "border-gray-300" : "border-red-500"
              }`}
            />
            {!emailValid && (
              <p className="text-sm text-red-600 mt-1">
                This id is not allowed to register
              </p>
            )}
          </div>

          {/* --- PHONE NUMBER FIELD (ADDED) --- */}
          <div>
            <label className="block text-sm mb-1 font-medium">
              Phone Number
            </label>
            <input
              type="tel"
              value={form.phoneno}
              onChange={(e) => setForm({ ...form, phoneno: e.target.value })}
              placeholder="Enter your phone number (Optional)"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#04254d]"
            />
          </div>
          {/* ---------------------------------- */}

          <div className="relative">
            <label className="block text-sm mb-1 font-medium">Password</label>
            <input
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              placeholder="Enter a strong password"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#04254d]"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute mt-3 right-3 top-1/2 transform -translate-y-1/2 text-gray-600"
            >
              {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm mb-1 font-medium">
              Select Role
            </label>
            <select
              value={form.role}
              onChange={(e) => {
                setForm({ ...form, role: e.target.value });
                setLinkingMode("");
                setRegistrationMode("");
              }}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#04254d]"
            >
              <option value="" disabled>
                -- Select Role --
              </option>
              <option value="parent">Parent</option>
              <option value="student">Student</option>
              <option value="teacher">Teacher</option>
              <option value="school_admin">School Admin</option>
            </select>
          </div>

          {/* Teacher Class Selection */}
          {form.role === "teacher" && (
            <div className="mb-4">
              <label className="block text-sm mb-1 font-medium">
                Select Class
              </label>
              <select
                value={form.class}
                onChange={(e) => setForm({ ...form, class: e.target.value })}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#04254d]"
              >
                <option value="" disabled>
                  -- Select Class --
                </option>
                {[3, 4, 5, 6, 7, 8, 9, 10].map((cls) => (
                  <option key={cls} value={cls}>
                    Class {cls}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* STUDENT FLOW */}
          {form.role === "student" && (
            <>
              {/* Student Registration Mode */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-3">
                  How would you like to register?
                </h3>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="studentMode"
                      value="independent"
                      checked={registrationMode === "independent"}
                      onChange={(e) => setRegistrationMode(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      Register independently (I'll share my student ID with my
                      parents later)
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="studentMode"
                      value="join_invitation"
                      checked={registrationMode === "join_invitation"}
                      onChange={(e) => setRegistrationMode(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      Join using parent's invitation code
                    </span>
                  </label>
                </div>
              </div>

              {/* Class Selection (always required for students) */}
              <div>
                <label className="block text-sm mb-1 font-medium">Class</label>
                <select
                  value={form.class}
                  onChange={(e) => setForm({ ...form, class: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#04254d]"
                >
                  <option value="" disabled>
                    Select Class
                  </option>
                  {[ 6, 7, 8, 9, 10].map((cls) => (
                    <option key={cls} value={cls}>
                      Class {cls}
                    </option>
                  ))}
                </select>
              </div>

              {/* Invitation Code (if joining via invitation) */}
              {registrationMode === "join_invitation" && (
                <div>
                  <label className="block text-sm mb-1 font-medium">
                    Parent Invitation Code
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={form.invitationCode}
                      onChange={handleInvitationCodeChange}
                      required
                      placeholder="Enter the invitation code from your parent"
                      className={`w-full px-4 py-2 pr-10 border rounded-md focus:ring-2 focus:ring-[#04254d] ${
                        validation.invitationCode.isValid === true
                          ? "border-green-500"
                          : validation.invitationCode.isValid === false
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {isValidating && (
                      <div className="absolute right-3 top-2.5">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#04254d]"></div>
                      </div>
                    )}
                  </div>
                  {validation.invitationCode.message && (
                    <p
                      className={`text-sm mt-1 ${
                        validation.invitationCode.isValid
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {validation.invitationCode.message}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* PARENT FLOW */}
          {form.role === "parent" && (
            <>
              {/* Parent Linking Mode */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-medium text-green-900 mb-3">
                  How would you like to connect with your child?
                </h3>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="parentMode"
                      value="existing"
                      checked={linkingMode === "existing"}
                      onChange={(e) => setLinkingMode(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      My child already has a student account (I have their
                      student ID)
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="parentMode"
                      value="create_invite"
                      checked={linkingMode === "create_invite"}
                      onChange={(e) => setLinkingMode(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      My child doesn't have an account yet (I'll invite them to
                      register)
                    </span>
                  </label>
                </div>
              </div>

              {/* Relationship Type */}
              <div>
                <label className="block text-sm mb-1 font-medium">
                  Relationship to Student
                </label>
                <select
                  value={form.relationshipType}
                  onChange={(e) =>
                    setForm({ ...form, relationshipType: e.target.value })
                  }
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#04254d]"
                >
                  <option value="father">Father</option>
                  <option value="mother">Mother</option>
                  <option value="guardian">Guardian</option>
                </select>
              </div>

              {/* Link to Existing Student */}
              {linkingMode === "existing" && (
                <div>
                  <label className="block text-sm mb-1 font-medium">
                    Student ID
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={form.studentId}
                      onChange={handleStudentIdChange}
                      required
                      placeholder="Enter your child's student ID (e.g., STU2024001)"
                      className={`w-full px-4 py-2 pr-10 border rounded-md focus:ring-2 focus:ring-[#04254d] ${
                        validation.studentId.isValid === true
                          ? "border-green-500"
                          : validation.studentId.isValid === false
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {isValidating && (
                      <div className="absolute right-3 top-2.5">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#04254d]"></div>
                      </div>
                    )}
                  </div>
                  {validation.studentId.message && (
                    <p
                      className={`text-sm mt-1 ${
                        validation.studentId.isValid
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {validation.studentId.message}
                    </p>
                  )}
                </div>
              )}

              {/* Create Invitation for New Student */}
              {linkingMode === "create_invite" && (
                <>
                  <div>
                    <label className="block text-sm mb-1 font-medium">
                      Child's Name
                    </label>
                    <input
                      type="text"
                      value={form.childName}
                      onChange={(e) =>
                        setForm({ ...form, childName: e.target.value })
                      }
                      required
                      placeholder="Enter your child's full name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#04254d]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm mb-1 font-medium">
                      Child's Class
                    </label>
                    <select
                      value={form.childClass}
                      onChange={(e) =>
                        setForm({ ...form, childClass: e.target.value })
                      }
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#04254d]"
                    >
                      <option value="" disabled>
                        Select Class
                      </option>
                      {[3, 4, 5, 6, 7, 8, 9, 10].map((cls) => (
                        <option key={cls} value={cls}>
                          Class {cls}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm mb-1 font-medium">
                      Child's Expected Email (Optional)
                    </label>
                    <input
                      type="email"
                      value={form.expectedChildEmail}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          expectedChildEmail: e.target.value,
                        })
                      }
                      placeholder="Your child's email address (if known)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#04254d]"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      This helps us verify your child when they register using
                      your invitation
                    </p>
                  </div>
                </>
              )}
            </>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={
              // form.role === "teacher" ||
              (form.role === "parent" &&
                linkingMode === "existing" &&
                validation.studentId.isValid !== true) ||
              (form.role === "student" &&
                registrationMode === "join_invitation" &&
                validation.invitationCode.isValid !== true)
            }
            className={`w-full py-3 rounded-md font-semibold transition-colors ${
              // form.role === "teacher" ||
              (form.role === "parent" &&
                linkingMode === "existing" &&
                validation.studentId.isValid !== true) ||
              (form.role === "student" &&
                registrationMode === "join_invitation" &&
                validation.invitationCode.isValid !== true)
                ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                : "bg-[#04254d] text-white hover:bg-[#0c1c5c]"
            }`}
          >
            Create Account
          </button>
        </form>
      </div>
    </div>
  );
}
